import { action, computed, observable } from 'mobx'
import path from 'ramda/src/path'
import { analyticsTracker } from '@winnin/analytics'

import { ALL_LANGUAGES } from 'consts'
import { PUBLIC_PROVIDERS } from 'consts/providers'
import { USER_ROLES } from 'consts/userRoles'
import { mapAllowedModulesToPermissionModules } from 'utils/allowed-modules'
import localStorageUtils from 'utils/localStorageUtils'

class UserStore {
  @observable userInfo = {}
  @observable providers = PUBLIC_PROVIDERS
  @observable terms = null
  @observable mixpanelLogEvents = analyticsTracker.isDebugEnabled
  @observable userLanguageOptsWithAll = []
  @observable userLanguageOptions = []
  @observable languageDefault = null
  @observable isFirstAccess = false
  @observable shouldReloadAfterRouterChange = false
  @observable reportId = null
  @observable urlParams = null
  @observable isImpersonating = false

  @action setIsImpersonating(isImpersonating) {
    this.isImpersonating = isImpersonating
  }

  @action findWorkspace = (workspaces, id) => {
    const brand = (workspaces ?? []).find(brand => brand.id === id)
    return brand
  }

  @action setUser(user) {
    const userActiveWorkspaces = user?.brands?.filter(brand => brand.status) || []

    localStorageUtils.setObject('companyId', user?.company?.id)

    const providersList = user?.company?.companyPlan?.providers || user?.company?.plan?.providers

    this.providers = providersList

    const isWinner = user?.email?.includes('@winnin') || user?.company?.id === 'WINNIN'

    this.userInfo = {
      ...user,
      brands: userActiveWorkspaces,
      isWinner,
    }
  }

  @action updateUserInfo(newInfo) {
    this.userInfo = {
      ...this.userInfo,
      ...newInfo,
    }
  }

  @action setReportId(reportId) {
    return (this.reportId = reportId)
  }

  @action setUserLanguages(languages) {
    const allOpt = { label: 'All', value: '', disabled: false }
    const defaultLanguageOpts = ALL_LANGUAGES.map(language => {
      if (languages.includes(language.value)) {
        return { label: language.label, value: language.value, disabled: false }
      } else {
        return { label: language.label, value: language.value, disabled: true }
      }
    })
    const languageOptsWithAll = [allOpt, ...defaultLanguageOpts]

    const defaultEnabledLanguages = defaultLanguageOpts.filter(language => !language.disabled)

    if (defaultLanguageOpts.length) {
      this.userLanguageOptions = defaultLanguageOpts
      this.userLanguageOptsWithAll = languageOptsWithAll
      this.languageDefault = defaultEnabledLanguages[0]
    }
  }

  @action hasUserInfo() {
    return !!Object.keys(this.userInfo).length
  }

  @computed get getRole() {
    const role = this.userInfo.role

    switch (role) {
      case USER_ROLES.VIEWER:
        return 'viewer'

      case USER_ROLES.WINNIN_LITE:
        return 'winnin-lite'

      case USER_ROLES.EDITOR:
        return 'editor'

      case USER_ROLES.COMPANY_ADMIN:
        return 'company-admin'

      case USER_ROLES.ADMIN:
        return 'admin'

      case USER_ROLES.GUEST:
        return 'guest'

      default:
        return 'unknown role'
    }
  }

  @computed get getActiveWorkspaceModules() {
    const workspaces = this.userInfo.workspaces ?? this.userInfo.brands ?? []
    const activeId = this.urlParams?.panelId || localStorageUtils.getObject('panelId')
    const workspace = this.findWorkspace(workspaces, activeId)
    const allowedModules = workspace?.workspaceSettings?.allowedModules ?? []
    return mapAllowedModulesToPermissionModules(allowedModules)
  }

  @action get(key) {
    if (typeof key === 'string') return path([key], this.userInfo)
    return path(key, this.userInfo)
  }

  @action toggleMixpanelLogEvents() {
    const next = !this.mixpanelLogEvents
    analyticsTracker.setDebug(next)
    this.mixpanelLogEvents = next
  }

  @action setUrlParams(routingStore) {
    this.urlParams = routingStore
  }
}

export default new UserStore()
